import { useEffect, useState } from 'react';
import { format, subMonths, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { supabase } from '../services/supabase';
import { leerVarias, listaEnCastellano } from '../lib/leerVarias';
import { ESTADOS_COBRADOS } from '../lib/ingresos';
import type { Database } from '../types/database.types';

// Shapes de las 3 queries de `loadStats`. Embeds anidados — el cliente sin
// <Database> infiere los embeds como arrays, así que el doble cast vía
// `unknown` es necesario (mismo patrón que la 5.4a/5.6).
type UserCreditRow = Database['public']['Tables']['user_credits']['Row'];
type SessionRow = Database['public']['Tables']['sessions']['Row'];
type EnrollmentRow = Database['public']['Tables']['enrollments']['Row'];

type CreditWithPack = Pick<UserCreditRow, 'purchase_date'> & {
  session_packs: { name: string; price_cents: number } | null;
};
type SessionBrief = Pick<SessionRow, 'date' | 'status'>;
type EnrollmentWithProfile = Pick<EnrollmentRow, 'status' | 'attendance' | 'user_id'> & {
  profiles: { full_name: string } | null;
};

// ESTA PÁGINA NO PINTA HUECOS: PINTA CIFRAS, Y UN CERO ES UNA AFIRMACIÓN.
//
// Con las seis lecturas tirando el `error`, la página salía entera y decía
// «€0 de ingresos», «0 sesiones», «Sin ventas este mes» y —la peor— «0% de
// cancelación» EN VERDE, porque el color lo elige `tasaCancelacion > 20`. Un
// fallo de RLS o de red se leía como un negocio parado y sano a la vez, y
// sobre eso se decide.
//
// Cada consulta trae su nombre para poder decir CUÁL no se pudo leer, y cada
// bloque cae por su cuenta: que no respondan los pedidos no borra los
// ingresos de seis meses.
const QUE = {
  ingresos: 'los ingresos',
  sesiones: 'las sesiones',
  inscripciones: 'las inscripciones',
  promos: 'las promociones',
  usos: 'los usos de promociones',
  pedidos: 'los pedidos del mes',
} as const;

const COLORS = ['#FFF200', '#C0C0C0', '#00C853', '#FF9800', '#FF4444'];
const MONTHS = Array.from({ length: 6 }, (_, i) => {
  const d = subMonths(new Date(), 5 - i);
  return { label: format(d, 'MMM', { locale: es }), start: startOfMonth(d).toISOString(), d };
});

interface MonthData {
  mes: string;
  ingresos: number;
  sesiones: number;
}
interface PackData {
  name: string;
  value: number;
}
interface AlumnoTop {
  name: string;
  asistencias: number;
}

// PR-UX2 — top productos vendidos (combinado): tipo legible por fila para que
// el operador entienda qué es cada línea.
const TYPE_LABELS: Record<string, string> = {
  physical_stock: 'Producto físico',
  membership: 'Matrícula',
  subscription: 'Suscripción',
  service: 'Vídeo',
  bundle: 'Pack combinado',
  bono: 'Bono',
};

interface ProductoTop {
  name: string;
  tipo: string;
  units: number;
}
interface PromoStats {
  usedCodes: number;
  totalCodes: number;
  redemptions: number;
}

// Snapshot de línea de pedido (orders.items). Solo los campos que usamos aquí.
interface OrderItem {
  name?: string;
  quantity?: number;
  fulfillment_type?: string;
  kind?: 'session_pack' | 'product';
}

export default function EstadisticasPage() {
  const [monthData, setMonthData] = useState<MonthData[]>([]);
  const [packData, setPackData] = useState<PackData[]>([]);
  const [alumnosTop, setAlumnosTop] = useState<AlumnoTop[]>([]);
  const [tasaCancelacion, setTasaCancelacion] = useState(0);
  const [topProductos, setTopProductos] = useState<ProductoTop[]>([]);
  const [promoStats, setPromoStats] = useState<PromoStats>({
    usedCodes: 0,
    totalCodes: 0,
    redemptions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [fallidos, setFallidos] = useState<string[]>([]);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    const treintaDias = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const { datos, fallidos: noLeidos } = await leerVarias([
      {
        que: QUE.ingresos,
        consulta: supabase
          .from('user_credits')
          .select('purchase_date, session_packs(name, price_cents)')
          .gte('purchase_date', MONTHS[0].start),
      },
      {
        que: QUE.sesiones,
        consulta: supabase
          .from('sessions')
          .select('date, status')
          .gte('date', format(MONTHS[0].d, 'yyyy-MM-dd')),
      },
      {
        que: QUE.inscripciones,
        consulta: supabase
          .from('enrollments')
          .select('status, attendance, user_id, profiles!enrollments_user_id_fkey(full_name)')
          .neq('is_secondary_group', true),
      },
      // PR-UX2 — promos creadas (todas, sin filtrar por active).
      { que: QUE.promos, consulta: supabase.from('discount_codes').select('id') },
      // PR-UX2 — redenciones de promos (cada uso) → códigos usados distintos.
      { que: QUE.usos, consulta: supabase.from('discount_code_uses').select('code_id') },
      // PR-UX2 — pedidos vendidos del último mes para el top de productos.
      //
      // El filtro es por lista BLANCA de estados, y tiene que seguir siéndolo.
      // [dashboard] La lista salió a `lib/ingresos.ts` cuando el dashboard
      // resultó estar calculando los ingresos de otra tabla: esta pantalla
      // decía la verdad y la otra no, y dos listas con el mismo nombre es
      // exactamente cómo se llega a eso. El porqué de cada estado está allí.
      // Un carrito abandonado ('pending', y 'expired' desde la mig 240) nunca
      // ha sido una venta y aquí no entra. Medido: nunca entró — la única vía
      // era que alguien le diera a "Procesar" en el panel, y ese botón ya no
      // existe para un pedido sin pagar.
      //
      // NO cambiar esto por "los que tienen payment intent": los canjes
      // gratuitos de `redeem_free_cart` nacen 'delivered' a 0 € y SIN intent.
      // Ese filtro borraría ventas reales del top de productos.
      {
        que: QUE.pedidos,
        consulta: supabase
          .from('orders')
          .select('items, status, created_at')
          .in('status', [...ESTADOS_COBRADOS])
          .gte('created_at', treintaDias),
      },
    ]);
    setFallidos(noLeidos);
    const [creditsRaw, sessionsRaw, enrollmentsRaw, codesRaw, usesRaw, ordersRaw] = datos;

    const credits = (creditsRaw ?? []) as unknown as CreditWithPack[];
    const sessions = (sessionsRaw ?? []) as SessionBrief[];
    const enrollments = (enrollmentsRaw ?? []) as unknown as EnrollmentWithProfile[];

    // KPI promos: cuántos códigos creados se han usado al menos una vez.
    const codes = (codesRaw ?? []) as { id: string }[];
    const uses = (usesRaw ?? []) as { code_id: string }[];
    const usedCodeIds = new Set(uses.map((u) => u.code_id));
    setPromoStats({
      usedCodes: usedCodeIds.size,
      totalCodes: codes.length,
      redemptions: uses.length,
    });

    // Top 10 productos vendidos del mes (combinado: físico/bono/vídeo/etc.).
    const prodCount: Record<string, ProductoTop> = {};
    const orders = (ordersRaw ?? []) as { items: OrderItem[] | null }[];
    orders.forEach((o) => {
      (o.items ?? []).forEach((it) => {
        const name = it.name ?? 'Sin nombre';
        const tipoKey =
          it.kind === 'session_pack' ? 'bono' : (it.fulfillment_type ?? 'physical_stock');
        const tipo = TYPE_LABELS[tipoKey] ?? TYPE_LABELS.physical_stock;
        const units = Math.max(1, Number(it.quantity) || 1);
        const key = `${name}__${tipo}`;
        if (!prodCount[key]) prodCount[key] = { name, tipo, units: 0 };
        prodCount[key].units += units;
      });
    });
    setTopProductos(
      Object.values(prodCount)
        .sort((a, b) => b.units - a.units)
        .slice(0, 10)
    );

    // Datos por mes
    const mData = MONTHS.map(({ label, start, d }) => {
      const nextMonth = startOfMonth(
        subMonths(new Date(), 5 - MONTHS.indexOf(MONTHS.find((m) => m.label === label)!))
      ).toISOString();
      const monthCredits = credits.filter((c) => {
        const pd = new Date(c.purchase_date);
        return pd >= new Date(start) && pd < new Date(nextMonth);
      });
      const monthSessions = sessions.filter((s) => {
        const sd = new Date(s.date);
        return (
          sd >= d &&
          sd < subMonths(new Date(), 5 - MONTHS.indexOf(MONTHS.find((m) => m.label === label)!) - 1)
        );
      });
      return {
        mes: label,
        ingresos: monthCredits.reduce((s, c) => s + (c.session_packs?.price_cents ?? 0) / 100, 0),
        sesiones: monthSessions.length,
      };
    });
    setMonthData(mData);

    // Pack más vendido
    const packCount: Record<string, number> = {};
    credits.forEach((c) => {
      const name = c.session_packs?.name ?? 'Otro';
      packCount[name] = (packCount[name] ?? 0) + 1;
    });
    setPackData(Object.entries(packCount).map(([name, value]) => ({ name, value })));

    // Top alumnos
    const byUser: Record<string, { name: string; count: number }> = {};
    enrollments
      .filter((e) => e.attendance === true)
      .forEach((e) => {
        if (!byUser[e.user_id])
          byUser[e.user_id] = { name: e.profiles?.full_name ?? '—', count: 0 };
        byUser[e.user_id].count++;
      });
    setAlumnosTop(
      Object.values(byUser)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
        .map(({ name, count }) => ({ name, asistencias: count }))
    );

    // Tasa de cancelación
    const total = enrollments.length;
    const canceladas = enrollments.filter((e) => e.status === 'cancelled').length;
    setTasaCancelacion(total > 0 ? Math.round((canceladas / total) * 100) : 0);

    setLoading(false);
  }

  if (loading)
    return (
      <div className="flex justify-center h-64 items-center">
        <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
      </div>
    );

  const fallo = (que: string) => fallidos.includes(que);

  return (
    <div className="space-y-8">
      {fallidos.length > 0 && (
        <div
          className="text-error text-sm font-inter bg-error/10 border border-error/30 rounded-lg px-4 py-3"
          data-testid="estad-lecturas-fallidas"
        >
          No se han podido leer {listaEnCastellano(fallidos)}. Los apartados que dependen de esos
          datos aparecen sin cifra: lo que se ve no es un cero, es que no se ha podido contar.
        </div>
      )}

      {/* KPIs simples */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="kpi-card">
          <p className="label">Ingresos totales (6M)</p>
          <p className="font-oswald text-2xl text-white" data-testid="estad-kpi-ingresos">
            {fallo(QUE.ingresos)
              ? '—'
              : `€${monthData.reduce((s, m) => s + m.ingresos, 0).toFixed(0)}`}
          </p>
        </div>
        <div className="kpi-card">
          <p className="label">Sesiones celebradas (6M)</p>
          <p className="font-oswald text-2xl text-white" data-testid="estad-kpi-sesiones">
            {fallo(QUE.sesiones) ? '—' : monthData.reduce((s, m) => s + m.sesiones, 0)}
          </p>
        </div>
        <div className="kpi-card">
          <p className="label">Tasa de cancelación</p>
          {/* El color lo decidía `tasaCancelacion > 20`, así que un fallo de
              lectura pintaba «0%» en VERDE: la cifra más tranquilizadora de la
              página era la que no se había podido calcular. Sin dato, ni cifra
              ni color. */}
          <p
            className={`font-oswald text-2xl ${
              fallo(QUE.inscripciones)
                ? 'text-text-secondary'
                : tasaCancelacion > 20
                  ? 'text-error'
                  : 'text-success'
            }`}
            data-testid="estad-kpi-cancelacion"
          >
            {fallo(QUE.inscripciones) ? '—' : `${tasaCancelacion}%`}
          </p>
        </div>
        <div className="kpi-card">
          <p className="label">Packs más vendido</p>
          <p className="font-oswald text-xl text-gold-primary" data-testid="estad-kpi-pack">
            {fallo(QUE.ingresos) ? '—' : (packData[0]?.name ?? '—')}
          </p>
        </div>
      </div>

      {/* Línea ingresos + barras sesiones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
            Ingresos mensuales (€)
          </h3>
          {/* Sin lectura, `monthData` trae seis meses a 0 y la gráfica pinta
              una línea plana en el suelo: un «no se ha vendido nada» dibujado,
              que convence más que un número. */}
          {fallo(QUE.ingresos) ? (
            <p
              className="text-text-secondary text-sm"
              data-testid="estad-grafica-ingresos-sin-datos"
            >
              No se han podido leer estos datos.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={monthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis dataKey="mes" tick={{ fill: '#A0A0A0', fontSize: 12 }} />
                <YAxis tick={{ fill: '#A0A0A0', fontSize: 12 }} tickFormatter={(v) => `€${v}`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #2A2A2A',
                    borderRadius: 8,
                  }}
                  formatter={(v: number) => [`€${v.toFixed(2)}`, 'Ingresos']}
                />
                <Line
                  type="monotone"
                  dataKey="ingresos"
                  stroke="#FFF200"
                  strokeWidth={2}
                  dot={{ fill: '#FFF200' }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="card">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
            Sesiones por mes
          </h3>
          {fallo(QUE.sesiones) ? (
            <p
              className="text-text-secondary text-sm"
              data-testid="estad-grafica-sesiones-sin-datos"
            >
              No se han podido leer estos datos.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                <XAxis dataKey="mes" tick={{ fill: '#A0A0A0', fontSize: 12 }} />
                <YAxis tick={{ fill: '#A0A0A0', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #2A2A2A',
                    borderRadius: 8,
                  }}
                />
                <Bar dataKey="sesiones" fill="#C0C0C0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Pack distribution + Top alumnos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
            Distribución por pack
          </h3>
          {fallo(QUE.ingresos) ? (
            <p className="text-text-secondary text-sm" data-testid="estad-packs-sin-datos">
              No se han podido leer estos datos.
            </p>
          ) : packData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={packData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                  fontSize={11}
                >
                  {packData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #2A2A2A',
                    borderRadius: 8,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-text-secondary text-sm">Sin datos</p>
          )}
        </div>
        <div className="card">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
            Top 10 alumnos más activos
          </h3>
          {fallo(QUE.inscripciones) && (
            <p className="text-text-secondary text-sm" data-testid="estad-alumnos-sin-datos">
              No se han podido leer estos datos.
            </p>
          )}
          {alumnosTop.map((a, i) => (
            <div
              key={i}
              className="flex items-center justify-between py-1.5 border-b border-bg-border last:border-0"
            >
              <div className="flex items-center gap-3">
                <span
                  className={`font-oswald text-sm w-6 ${i < 3 ? 'text-gold-primary' : 'text-text-secondary'}`}
                >
                  {i + 1}
                </span>
                <p className="text-white text-sm">{a.name}</p>
              </div>
              <span className="badge-gold">{a.asistencias} sesiones</span>
            </div>
          ))}
        </div>
      </div>

      {/* PR-UX2 — Promociones + Top productos del mes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
            Promociones
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="kpi-card" data-testid="estad-kpi-promos">
              <p className="label">Promos usadas</p>
              <p className="font-oswald text-2xl text-white">
                {fallo(QUE.promos) || fallo(QUE.usos) ? (
                  '—'
                ) : (
                  <>
                    {promoStats.usedCodes}
                    <span className="text-text-secondary text-lg"> / {promoStats.totalCodes}</span>
                  </>
                )}
              </p>
              <p className="text-text-secondary text-xs font-inter mt-1">
                códigos creados con al menos un uso
              </p>
            </div>
            <div className="kpi-card" data-testid="estad-kpi-redenciones">
              <p className="label">Redenciones totales</p>
              <p className="font-oswald text-2xl text-gold-primary">
                {fallo(QUE.usos) ? '—' : promoStats.redemptions}
              </p>
              <p className="text-text-secondary text-xs font-inter mt-1">usos de promociones</p>
            </div>
          </div>
        </div>

        <div className="card" data-testid="estad-top-productos">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
            Top 10 productos del mes
          </h3>
          {topProductos.length > 0 ? (
            topProductos.map((p, i) => (
              <div
                key={`${p.name}-${p.tipo}`}
                data-testid={`estad-producto-row-${i}`}
                className="flex items-center justify-between py-1.5 border-b border-bg-border last:border-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={`font-oswald text-sm w-6 ${i < 3 ? 'text-gold-primary' : 'text-text-secondary'}`}
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{p.name}</p>
                    <p className="text-text-secondary text-xs font-inter">{p.tipo}</p>
                  </div>
                </div>
                <span className="badge-gold whitespace-nowrap">{p.units} ud.</span>
              </div>
            ))
          ) : (
            <p className="text-text-secondary text-sm" data-testid="estad-productos-vacio">
              {/* «Sin ventas este mes» es una afirmación sobre el negocio, no
                  sobre la consulta. */}
              {fallo(QUE.pedidos)
                ? 'No se han podido leer los pedidos de este mes.'
                : 'Sin ventas este mes'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
